@if (initialized) {
    <div class="cn-reconciliation-panel">

        <!-- ===================== 1) EMPFANGENE PROVISIONEN ERFASSEN ===================== -->
        <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
            <div class="text-lg font-medium mb-3">Empfangene Provisionen erfassen</div>

            <!-- Beleg-Kopf: Partner / Stichtag / Periode -->
            <div class="grid align-items-end">
                <div class="col-12 md:col-4">
                    <label class="text-sm text-600" for="cn-rec-partner">Partner (Pflicht)</label>
                    <p-select id="cn-rec-partner"
                              [(ngModel)]="statementPartnerId"
                              [options]="partnerOptions"
                              optionLabel="label" optionValue="value"
                              [filter]="true" filterBy="label"
                              placeholder="Partner wählen"
                              [disabled]="statementState === 'ready'"
                              styleClass="w-full">
                    </p-select>
                </div>
                <div class="col-6 md:col-4">
                    <label class="text-sm text-600" for="cn-rec-date">Stichtag (Pflicht)</label>
                    <p-datePicker id="cn-rec-date"
                                  [(ngModel)]="statementDate"
                                  dateFormat="dd.mm.yy"
                                  [showIcon]="true"
                                  [disabled]="statementState === 'ready'"
                                  styleClass="w-full">
                    </p-datePicker>
                </div>
                <div class="col-6 md:col-4">
                    <label class="text-sm text-600" for="cn-rec-period">Periode (Pflicht)</label>
                    <p-datePicker id="cn-rec-period"
                                  [(ngModel)]="statementPeriodDate"
                                  view="month"
                                  dateFormat="mm.yy"
                                  [showIcon]="true"
                                  [showButtonBar]="true"
                                  placeholder="Monat wählen"
                                  [disabled]="statementState === 'ready'"
                                  styleClass="w-full">
                    </p-datePicker>
                </div>
            </div>

            @if (statementError) {
                <p-message severity="error" class="block mt-3" [text]="statementError"></p-message>
            }

            @if (statementState !== 'ready') {
                <div class="flex justify-content-end mt-3">
                    <p-button label="Beleg anlegen"
                              icon="fa-regular fa-file-circle-plus"
                              [disabled]="!canCreateStatement()"
                              [loading]="statementState === 'busy'"
                              (onClick)="createStatement()">
                    </p-button>
                </div>
            }

            <!-- Angelegter Beleg + Positions-Erfassung -->
            @if (statementState === 'ready' && statement) {
                <div class="surface-50 border-round p-3 mt-3">
                    <div class="flex align-items-center gap-2 mb-3 flex-wrap">
                        <i class="fa-regular fa-circle-check text-green-600"></i>
                        <span class="font-medium">Beleg #{{ statement.id }} angelegt</span>
                        <span class="text-sm text-600">
                            @if (statement.partnerId != null) {
                                {{ partnerName(statement.partnerId) }}
                            } @else {
                                Partner unbekannt
                            }
                            · Periode {{ statement.period }}
                        </span>
                    </div>

                    <div class="text-sm font-medium mb-2">Position hinzufügen (Neuzugang / Storno)</div>
                    <div class="grid align-items-end">
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-vsnr">VSNR</label>
                            <input id="cn-line-vsnr" pInputText [(ngModel)]="lineVsnr" class="w-full"/>
                        </div>
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-tariff">Tarif (Pflicht)</label>
                            <input id="cn-line-tariff" pInputText [(ngModel)]="lineTariffKey" class="w-full"/>
                        </div>
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-booking">Buchungsperiode</label>
                            <input id="cn-line-booking" pInputText [(ngModel)]="lineBookingPeriod"
                                   placeholder="JJJJ-MM" class="w-full"/>
                        </div>
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-amount">Betrag (€)</label>
                            <p-inputNumber id="cn-line-amount"
                                           [(ngModel)]="lineAmount"
                                           mode="currency" currency="EUR" locale="de-DE"
                                           styleClass="w-full">
                            </p-inputNumber>
                        </div>
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-trigger">Trigger</label>
                            <p-select id="cn-line-trigger"
                                      [(ngModel)]="lineTrigger"
                                      [options]="triggerOptions"
                                      optionLabel="label" optionValue="value"
                                      styleClass="w-full">
                            </p-select>
                        </div>
                        <div class="col-6 md:col-2">
                            <label class="text-sm text-600" for="cn-line-contract">Vertrags-ID</label>
                            <p-inputNumber id="cn-line-contract"
                                           [(ngModel)]="lineContractId"
                                           [useGrouping]="false"
                                           styleClass="w-full">
                            </p-inputNumber>
                        </div>
                    </div>

                    @if (lineError) {
                        <p-message severity="error" class="block mt-3" [text]="lineError"></p-message>
                    }

                    <div class="flex justify-content-end mt-3">
                        <p-button label="Position hinzufügen"
                                  icon="fa-regular fa-plus"
                                  size="small"
                                  [disabled]="!canAddLine()"
                                  [loading]="lineState === 'busy'"
                                  (onClick)="addLine()">
                        </p-button>
                    </div>

                    <!-- Erfasste Positionen -->
                    @if (statementLines.length) {
                        <div class="mt-3">
                            <div class="text-sm font-medium mb-2">Erfasste Positionen</div>
                            <p-table [value]="statementLines" styleClass="p-datatable-sm">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>VSNR</th>
                                        <th>Tarif</th>
                                        <th>Buchungsperiode</th>
                                        <th class="text-right">Betrag</th>
                                        <th>Trigger</th>
                                        <th class="text-right">Vertrags-ID</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td>{{ row.vsnr || '–' }}</td>
                                        <td>{{ row.tariffKey }}</td>
                                        <td>{{ row.bookingPeriod }}</td>
                                        <td class="text-right">{{ row.amount | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td>{{ triggerLabel(row.trigger) }}</td>
                                        <td class="text-right">{{ row.contractId ?? '–' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>
                    } @else {
                        <div class="text-600 text-sm mt-3">Noch keine Positionen erfasst.</div>
                    }
                </div>
            }
        </div>

        <!-- ===================== 2) ABGLEICH STARTEN ===================== -->
        <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
            <div class="text-lg font-medium mb-3">Abgleich starten</div>

            <div class="grid align-items-end">
                <div class="col-12 md:col-5">
                    <label class="text-sm text-600" for="cn-run-partner">Partner (Pflicht)</label>
                    <p-select id="cn-run-partner"
                              [(ngModel)]="runPartnerId"
                              [options]="partnerOptions"
                              optionLabel="label" optionValue="value"
                              [filter]="true" filterBy="label"
                              placeholder="Partner wählen"
                              styleClass="w-full">
                    </p-select>
                </div>
                <div class="col-12 md:col-5">
                    <label class="text-sm text-600" for="cn-run-period">Periode (Pflicht)</label>
                    <p-datePicker id="cn-run-period"
                                  [(ngModel)]="runPeriodDate"
                                  view="month"
                                  dateFormat="mm.yy"
                                  [showIcon]="true"
                                  [showButtonBar]="true"
                                  placeholder="Monat wählen"
                                  styleClass="w-full">
                    </p-datePicker>
                </div>
                <div class="col-12 md:col-2">
                    <p-button label="Abgleich starten"
                              icon="fa-regular fa-scale-balanced"
                              styleClass="w-full"
                              [disabled]="!canRunReconciliation()"
                              [loading]="runState === 'busy'"
                              (onClick)="runReconciliation()">
                    </p-button>
                </div>
            </div>

            <!-- Busy -->
            @if (runState === 'busy') {
                <div class="mt-3">
                    <div class="text-sm text-600 mb-2">Abgleich läuft…</div>
                    <p-progressBar mode="indeterminate" [style]="{height: '6px'}"></p-progressBar>
                </div>
            }

            <!-- Error -->
            @if (runState === 'error') {
                <p-message severity="error" class="block mt-3" [text]="runError"></p-message>
            }

            <!-- Ready: Summary + server-paged result table -->
            @if (runState === 'ready' && runResult) {
                <!-- FE-8: Ergebnis-Kacheln mit Klartext-Beschriftung; die Status-Begriffe sind
                     identisch zu den Tabellen-Badges und der Status-Legende. -->
                <div class="grid mt-3">
                    <div class="col-6 md:col-2">
                        <div class="surface-50 border-round p-2 text-center h-full"
                             pTooltip="Anzahl der eingelesenen Beleg-Positionen" tooltipPosition="top">
                            <div class="text-xs text-600">Verarbeitete Positionen</div>
                            <div class="text-xl font-bold">{{ runResult.processedLines | number:'1.0-0':'de' }}</div>
                        </div>
                    </div>
                    <div class="col-6 md:col-2">
                        <div class="surface-50 border-round p-2 text-center h-full"
                             pTooltip="Soll und Ist stimmen überein" tooltipPosition="top">
                            <div class="text-xs text-600">In Ordnung</div>
                            <div class="text-xl font-bold text-green-600">{{ runResult.okCount | number:'1.0-0':'de' }}</div>
                        </div>
                    </div>
                    <div class="col-6 md:col-2">
                        <div class="surface-50 border-round p-2 text-center h-full"
                             pTooltip="Unter- oder Überzahlung gegenüber der Erwartung" tooltipPosition="top">
                            <div class="text-xs text-600">Abweichung</div>
                            <div class="text-xl font-bold text-orange-500">{{ runResult.deltaCount | number:'1.0-0':'de' }}</div>
                        </div>
                    </div>
                    <div class="col-6 md:col-2">
                        <div class="surface-50 border-round p-2 text-center h-full"
                             pTooltip="Erwartete Provision wurde nicht empfangen" tooltipPosition="top">
                            <div class="text-xs text-600">Fehlend</div>
                            <div class="text-xl font-bold text-red-500">{{ runResult.missingCount | number:'1.0-0':'de' }}</div>
                        </div>
                    </div>
                    <div class="col-6 md:col-2">
                        <div class="surface-50 border-round p-2 text-center h-full"
                             pTooltip="Provision empfangen, zu der keine Erwartung vorliegt" tooltipPosition="top">
                            <div class="text-xs text-600">Unerwartet</div>
                            <div class="text-xl font-bold text-red-500">{{ runResult.unexpectedCount | number:'1.0-0':'de' }}</div>
                        </div>
                    </div>
                </div>

                @if (runResult.message) {
                    <div class="text-sm text-600 mt-2">{{ runResult.message }}</div>
                }

                <!-- Status-Legende: Klartext für die 7 Abgleich-Status (Simone W4).
                     Die Ergebnistabelle zeigt das deutsche uiLabel des Enums; diese Legende erklärt
                     jeden Status in Alltagssprache (Tooltip = ausführliche Erläuterung). -->
                <div class="mt-3">
                    <div class="text-sm font-medium mb-2">Status-Legende</div>
                    <div class="flex flex-wrap gap-2">
                        @for (entry of reconciliationStatusLegend; track entry.label) {
                            <span class="inline-flex align-items-center surface-100 border-round px-2 py-1 text-sm"
                                  [pTooltip]="entry.description" tooltipPosition="top">
                                <i class="fa-regular fa-circle-info text-600 mr-1"></i>{{ entry.label }}
                            </span>
                        }
                    </div>
                </div>

                <!-- Server-paged reconciliation rows (status badge from enum uiColor).
                     Empty state + loading skeleton are handled by mvs-widget. Select to drill down. -->
                <div class="mt-3">
                    <div class="text-sm font-medium mb-1">Abgleich-Ergebnisse je Erwartung</div>
                    <div class="text-xs text-600 mb-2">
                        Status, erwarteter Betrag und Differenz (Ist − Soll) pro Erwartung.
                        Zeile anklicken, um die einzelnen Buchungen (Komponenten) zu sehen.
                    </div>
                    @if (reconciliationWidget) {
                        <mvs-widget
                                [widgetData]="reconciliationWidget"
                                (onObjectSelect)="handleReconciliationSelect($event)">
                        </mvs-widget>
                    }
                </div>

                <!-- Drill-down: components of the selected reconciliation row -->
                @if (componentWidget) {
                    <div class="mt-3">
                        <div class="text-sm font-medium mb-1">Buchungen zur gewählten Erwartung</div>
                        <div class="text-xs text-600 mb-2">
                            Einzelne Ist-Buchungen, die in den gewählten Abgleich eingeflossen sind.
                        </div>
                        <mvs-widget [widgetData]="componentWidget"></mvs-widget>
                    </div>
                }
            }

            <!-- Idle / empty -->
            @if (runState === 'idle') {
                <div class="text-center text-600 p-4">
                    Geben Sie Partner und Periode an, um den Abgleich zu starten.
                </div>
            }
        </div>

        <!-- ===================== 3) MODELL-ABGLEICH ===================== -->
        <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
            <div class="text-lg font-medium mb-3">Modell-Abgleich</div>

            <div class="grid">
                <!-- Aktiv-Modell -->
                <div class="col-12 md:col-6">
                    <div class="text-sm text-600 mb-1">Aktiv-Modell (Pflicht)</div>
                    @if (abgleichActiveModelWidget) {
                        <mvs-widget
                                [widgetData]="abgleichActiveModelWidget"
                                (onObjectSelect)="handleAbgleichActiveModelSelect($event)">
                        </mvs-widget>
                    }
                </div>
                <!-- Szenario-Modelle -->
                <div class="col-12 md:col-6">
                    <div class="text-sm text-600 mb-1">Szenario-Modelle (Mehrfachauswahl)</div>
                    @if (abgleichScenarioModelWidget) {
                        <mvs-widget
                                [widgetData]="abgleichScenarioModelWidget"
                                (onObjectSelect)="handleAbgleichScenarioModelSelect($event)">
                        </mvs-widget>
                    }
                    @if (abgleichScenarioModelIds.length) {
                        <div class="text-xs text-600 mt-1">
                            {{ abgleichScenarioModelIds.length }} Szenario(s) ausgewählt
                        </div>
                    }
                </div>
            </div>

            <div class="grid mt-2 align-items-end">
                <div class="col-6 md:col-4">
                    <label class="text-sm text-600" for="cn-abg-statement">Beleg (Pflicht)</label>
                    <p-select id="cn-abg-statement"
                              [(ngModel)]="abgleichStatementId"
                              [options]="statementOptions"
                              optionLabel="label" optionValue="value"
                              [filter]="true" filterBy="label"
                              placeholder="Beleg wählen"
                              styleClass="w-full">
                    </p-select>
                </div>
                <div class="col-6 md:col-4">
                    <label class="text-sm text-600" for="cn-abg-horizon">Horizont (Jahre)</label>
                    <p-inputNumber id="cn-abg-horizon"
                                   [(ngModel)]="abgleichHorizonYears"
                                   [min]="1" [max]="30"
                                   styleClass="w-full">
                    </p-inputNumber>
                </div>
                <div class="col-12 md:col-4">
                    <p-button label="Modell-Abgleich starten"
                              icon="fa-regular fa-layer-group"
                              styleClass="w-full"
                              [disabled]="!canRunAbgleich()"
                              (onClick)="runAbgleich()">
                    </p-button>
                </div>
            </div>

            <!-- The rich Soll/Ist-Abgleich (Verdict, Summen-Kacheln, Je Vertrag, Verlauf) now opens on
                 the dedicated Modell-Vergleich page; this button navigates there with the selection. -->
            <div class="flex align-items-start gap-2 text-xs text-500 mt-3">
                <i class="fa-regular fa-circle-info mt-1"></i>
                <span>Der Modell-Vergleich öffnet sich auf der Seite „Modell-Vergleich" mit der hier getroffenen Auswahl.</span>
            </div>
        </div>

    </div>
}
